import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import { setting } from '../../../../setting';
import { JSEncrypt } from 'jsencrypt';
import { Observable, firstValueFrom, shareReplay } from 'rxjs';

@Injectable({ providedIn: 'root' })

export class LoginService {
	public apiBookingUrl :string;
  authApiUrl: string;

	constructor(private _http:HttpClient){
		this.apiBookingUrl  = setting.apiBookingUrl
    this.authApiUrl = setting.authApiUrl;

	}

  /**
 * Método para inicializar la clave pública
 *
 * Este método se utiliza para obtener y validar la clave pública desde el backend,
 * almacenándola para su uso posterior en operaciones de cifrado.
 * Implementa un patrón singleton para evitar múltiples llamadas al endpoint.
 *
 * @publicKey-> almacena la clave pública obtenida (string o null)
 * @publicKeyLoaded-> indica si la clave pública fue cargada correctamente (boolean)
 * @publicKeyPromise-> promesa que controla el estado de la carga (Promise<void> | null)
 */

private publicKey: string | null = null;
private publicKeyLoaded = false;
private publicKeyPromise: Promise<void> | null = null;

async initializePublicKey(): Promise<void> {
  if (!this.publicKeyPromise) {
    this.publicKeyPromise = (async () => {
      try {
        const response = await firstValueFrom(
          this._http.get(`${this.apiBookingUrl}public-key`, {
            headers: { 'Cache-Control': 'no-cache' },
            responseType: 'text'
          })
        );

        const cleanedKey = response
          .trim()
          .replace(/\r?\n|\r/g, '\n')
          .replace(/\\n/g, '\n');

        if (
          !cleanedKey.includes('BEGIN PUBLIC KEY') ||
          !cleanedKey.includes('END PUBLIC KEY')
        ) {
          throw new Error('Formato de clave pública inválido');
        }

        this.publicKey = cleanedKey;
        this.publicKeyLoaded = true;

      } catch (error) {
        console.error('Error obteniendo clave pública:', error);
        this.publicKeyPromise = null;
        throw error;
      }
    })();
  }

  return this.publicKeyPromise;
}

/**
 * Método para realizar el login del usuario
 *
 * Este método cifra la contraseña con la clave pública antes de enviarla al backend
 * e implementa validaciones adicionales del proceso de cifrado.
 *
 * @email-> correo electrónico del usuario (string)
 * @password-> contraseña del usuario (string)
 * @encrypt-> instancia de JSEncrypt para el cifrado RSA
 * @testEncryption-> prueba de cifrado inicial para validar la clave pública
 * @encryptedPass-> contraseña cifrada resultante (string)
 * @requestPayload-> objeto con los datos de login para enviar al backend
 */

/**
 * jsencrypt genera padding PKCS1v1.5 aleatorio en cada llamada a encrypt(); en un
 * porcentaje chico de intentos ese padding hace que la librería devuelva `false`
 * aunque la clave y el texto sean válidos (bug conocido de la librería). Reintentar
 * alcanza casi siempre, porque cada intento genera un padding nuevo.
 */
private encryptWithRetry(encrypt: JSEncrypt, text: string, attempts: number = 3): string | false {
  for (let i = 0; i < attempts; i++) {
    const result = encrypt.encrypt(text);
    if (result) {
      return result;
    }
  }
  return false;
}

async login(login, turnstileToken: string | null): Promise<any> {
  try {
    if (!this.publicKeyLoaded) {
      await this.initializePublicKey();
    }

    const encrypt = new JSEncrypt();

    // Versión alternativa para debug

    encrypt.setPublicKey(this.publicKey!);

    // Intenta con un texto simple primero
    const testEncryption = encrypt.encrypt('test');
    if (!testEncryption) {
      throw new Error('La clave no puede cifrar ni siquiera texto simple');
    }

    const encryptedPass = this.encryptWithRetry(encrypt, login.password);
    if (!encryptedPass) {
      throw new Error('Error en cifrado RSA - Clave inválida para este contenido');
    }

    const requestPayload = {
      email: login.email,
      password: encryptedPass,
      turnstile_token: turnstileToken
    };

    return firstValueFrom(
     this._http.post(`${this.apiBookingUrl}login`, requestPayload
      )
     );

  } catch (error) {
    console.error('Error completo en login:', {
      error: error,
      publicKeyState: {
        loaded: this.publicKeyLoaded,
        key: this.publicKey ? 'presente' : 'ausente',
        valid: this.publicKey?.includes('BEGIN PUBLIC KEY')
      }
    });
    throw error;
  }
}

/**
 * Segundo paso del login cuando el usuario tiene 2FA (TOTP / Google Authenticator) activado.
 *
 * Contrato esperado del backend (pendiente de implementar ahí, documentado acá para que
 * quede claro qué espera el frontend):
 *   - `login` (arriba) responde `{ res: 2, pending_token }` en vez de `{ res: 1, token }`
 *     cuando el usuario tiene 2FA activo. `pending_token` es de un solo uso y de vida corta,
 *     identifica ese intento de login pendiente (NO es el auth_token final).
 *   - Este endpoint recibe `{ pending_token, code }` (code = 6 dígitos del authenticator) y
 *     responde `{ res: 1, token }` igual que un login exitoso normal, o `{ res: 0 }` si el
 *     código es inválido/expiró.
 *   - El secreto TOTP y su verificación viven SIEMPRE en el backend; el frontend nunca debe
 *     validar el código localmente (eso anularía el segundo factor).
 */
async verifyTwoFactor(pendingToken: string, code: string): Promise<any> {
  return firstValueFrom(
    this._http.post(`${this.apiBookingUrl}login/2fa/verify`, {
      pending_token: pendingToken,
      code
    })
  );
}

	// login(login: any){ // login service
	// 	let headers = new HttpHeaders();
	// 	headers.append('Access-Control-Allow-Origin', '*');
	// 	headers.append('Access-Control-Allow-Credentials', 'true');
	// 	headers.append('Access-Control-Allow-Headers', 'Content-Type');
	// 	headers.append('Access-Control-Allow-Methods', 'GET, POST, OPTIONS, DELETE');
	// 	headers.append('Content-Type','application/json');
	// 	headers.append('Authorization','Bearer ' + login.auth_token);
	// 	return this._http.post(this.apiBookingUrl+'login', login, {headers: headers});
	// }


	getCurrent(){
		return this._http.get(this.apiBookingUrl+'user/current');
	}

	/**
	 * Al entrar a /select, tanto el AuthGuard (para decidir si te deja pasar)
	 * como SelectComponent (para pintar los datos) necesitan el mismo user/current.
	 * Se cachea el observable en vuelo para que sea un solo request, no dos.
	 * clearCurrentUserCache() lo invalida en logout.
	 */
	private currentUser$: Observable<any> | null = null;

	getCurrentCached(): Observable<any> {
		if (!this.currentUser$) {
			this.currentUser$ = this.getCurrent().pipe(shareReplay(1));
		}
		return this.currentUser$;
	}

	clearCurrentUserCache(): void {
		this.currentUser$ = null;
	}

  /**
   * Activa/desactiva 2FA (TOTP / Google Authenticator) para la cuenta, vía el mismo
   * endpoint genérico de edición de usuario que usa registerIP.
   *
   * TODO: pendiente confirmar con el backend el nombre exacto del campo (acá se asume
   * `two_factor_enabled`) y la forma de la respuesta al activar — se espera
   * `{ res: 1, data: { qr_image, secret } }` con `qr_image` como imagen base64 lista
   * para un <img>. Ajustar cuando esté la ruta/contrato final.
   */
  updateTwoFactor(data: { user_id: number; two_factor_enabled: boolean }): Observable<any> {
      return this._http.put<any>(`${this.apiBookingUrl}users/${data.user_id}`, data);
  }

  registerIP(data): Observable<any> {
      return this._http.put<any>(`${this.apiBookingUrl}users/${data.user_id}`, data);
  }

	listModule(data: any) {
		return this._http.get(this.apiBookingUrl + 'ListUserModule/' + data.email);
	}

	listToModulesUser(){
		return this._http.get(this.apiBookingUrl + 'application/listToModulesUser')
	}

	getProfileUser(){
		return this._http.get(this.apiBookingUrl + 'application/getProfileUser');
	}

	updAgency(idAgenncy:any)
	{
		let json = JSON.stringify(idAgenncy);
		return this._http.post(this.apiBookingUrl + 'application/updAgency', json);
	}

	getInfoAgency (idAgenncy: any) {
		let json = JSON.stringify(idAgenncy);
		return this._http.post(this.apiBookingUrl + 'application/getInfoAgency', json);
	}

	getAgency(data: any){
		return this._http.get(this.apiBookingUrl + '/agenciesUsers/' + data.user[0].id);
	}

	logout(data: any) {
		let json = JSON.stringify(data);
		return this._http.post(this.apiBookingUrl+'logout', json);
	}

	validate(token : any) { // validates if the session token is still valid.
		let json = JSON.stringify(token);
		return this._http.post(this.apiBookingUrl+'valid', json);
	}

/**
 * Restaurado solo para el modo legacy (setting.legacyAuthCookie): antes de
 * "cookie security" era el frontend el que guardaba el token que devolvía el
 * body del login, escribiéndolo acá en una cookie propia. Con el backend
 * seteando la cookie HttpOnly vía Set-Cookie esto no hace falta -- no llamar
 * si legacyAuthCookie está en false.
 */
setCookie(cname: string, cvalue: string, exdays: number | null, domain: string, secure = true) {
  let expires = '';
  if (exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    expires = 'expires=' + d.toUTCString();
  }

  let cookie = cname + '=' + cvalue + ';' + expires + ';domain=' + domain + ';path=/';
  if (secure && window.location.protocol === 'https:') {
    cookie += ';Secure';
  }

  document.cookie = cookie;
}

async verifyModule(module: string): Promise<any> {
  try {
    const response = await firstValueFrom( this._http.post(`${this.apiBookingUrl}/auth/verify`,
      { module: module },
      { withCredentials: true }
    )
  );
    return response;
  } catch (error) {
    console.error('Error verifying module:', error);
    throw error;
  }
}

  }
class ResponseCustom
{
	private response;

	constructor(response) {
		this.response = response;
	}

	getResponse() {
		return this.response;
	}

	hasError() {
		return this.response.cod != 200;
	}

	token() {
		return this.response.token;
	}





}


